/* Session 147j+16bp — First-login welcome tour for /dashboard.
 *
 * Lightweight, dependency-free modal. Shown ONCE per browser when a
 * user lands on /dashboard and `localStorage.dashboard_tour_seen` is
 * not set to "1". User can dismiss at any time via the Skip link or
 * the close button — both write the flag so we never bother them again.
 *
 * Replay path: `localStorage.removeItem('dashboard_tour_seen')` from
 * devtools console, OR a future "?tour=1" query-param trigger.
 *
 * 4 slides — keep copy tight, scannable, accurate:
 *   1. Welcome + scope
 *   2. AlphaScout — the 3 profiles
 *   3. LODR — verdict, dual-mode, quality grade
 *   4. How to drive the UI
 */
import React, { useState, useEffect } from 'react';
import {
  X, Target, FileText, ShieldCheck, Sparkles,
  ChevronLeft, ChevronRight, MousePointerClick,
} from 'lucide-react';

const TOUR_STORAGE_KEY = 'dashboard_tour_seen';

const Slide = ({ icon: Icon, title, children }) => (
  <div className="px-6 py-7" data-testid="welcome-tour-slide">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    </div>
    <div className="text-sm text-muted-foreground leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const Pill = ({ tone, children }) => {
  const tones = {
    emerald: 'border-emerald-400/60 text-emerald-200 bg-emerald-500/10',
    amber:   'border-amber-400/60 text-amber-200 bg-amber-500/10',
    rose:    'border-rose-400/60 text-rose-200 bg-rose-500/10',
    sky:     'border-sky-400/60 text-sky-200 bg-sky-500/10',
    zinc:    'border-zinc-400/60 text-zinc-200 bg-zinc-500/10',
  };
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded ${tones[tone] || tones.zinc}`}>
      {children}
    </span>
  );
};

const SLIDES = [
  {
    icon: Sparkles,
    title: 'Welcome — research dashboard',
    body: (
      <>
        <p>
          A daily-refreshed view of two signal sources we run for you:
        </p>
        <ul className="list-disc list-inside space-y-1 ml-1">
          <li><span className="text-foreground font-medium">AlphaScout</span> — ML-scored stock picks across 3 universes (Largecap, Mid &amp; Small, Value)</li>
          <li><span className="text-foreground font-medium">Order Wins</span> — every BSE/NSE corporate order announcement, scored by an LLM</li>
        </ul>
        <p className="text-[11px] italic">
          Read-only. No trading from this dashboard — use your broker for execution.
        </p>
      </>
    ),
  },
  {
    icon: Target,
    title: 'AlphaScout — three models, one job',
    body: (
      <>
        <p>Each profile picks ~10–15 stocks from a different universe:</p>
        <ul className="list-disc list-inside space-y-1.5 ml-1">
          <li><span className="text-foreground font-medium">Largecap</span> — Nifty-200 names. Stable, lower-risk.</li>
          <li><span className="text-foreground font-medium">Mid &amp; Small</span> — Mid + small cap (rank 200-490). Higher-alpha, higher-noise.</li>
          <li><span className="text-foreground font-medium">Value</span> — Deep-value × quality × contrarian intersection. Patient — months to play out.</li>
        </ul>
        <p>
          Higher <span className="text-foreground font-medium">composite score</span> = stronger signal.
          Our adaptive floor self-tunes per regime — you'll never sit with stale picks.
        </p>
      </>
    ),
  },
  {
    icon: FileText,
    title: 'Order Wins — reading a verdict',
    body: (
      <>
        <p>Each row is one corporate order announcement. Glance at:</p>
        <div className="space-y-2 mt-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-foreground font-medium w-20 shrink-0">Verdict:</span>
            <Pill tone="emerald">BUY</Pill>
            <Pill tone="amber">WATCH</Pill>
            <Pill tone="zinc">SKIP</Pill>
            <Pill tone="rose">AVOID</Pill>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-foreground font-medium w-20 shrink-0">Quality:</span>
            <Pill tone="emerald">A</Pill>
            <Pill tone="sky">B</Pill>
            <Pill tone="amber">C</Pill>
            <Pill tone="rose">D</Pill>
            <span className="text-[11px]">balance-sheet health</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-foreground font-medium w-20 shrink-0">Risk:</span>
            <Pill tone="rose">LEVERAGED</Pill>
            <Pill tone="sky">MARKET MOVING SIZE</Pill>
            <span className="text-[11px]">red vs positive</span>
          </div>
        </div>
        <p className="pt-2">
          Open any row's drawer to see the <span className="text-foreground font-medium">dual-mode verdict</span>:
          a <span className="text-emerald-300">Trader</span> view (1–5 day catalyst) and an
          {' '}<span className="text-sky-300">Investor</span> view (12–36 mo durability) — they can disagree.
        </p>
      </>
    ),
  },
  {
    icon: MousePointerClick,
    title: 'Driving the UI',
    body: (
      <>
        <ul className="list-disc list-inside space-y-1.5 ml-1">
          <li><span className="text-foreground font-medium">Click any Order Wins row</span> to expand the deep-analysis drawer (thesis, bull/bear cases, fundamentals).</li>
          <li><span className="text-foreground font-medium">Filters</span> at the top: confidence threshold, verdict, size tier, customer type, hot-streak.</li>
          <li>Data refreshes <span className="text-foreground font-medium">every 30 min</span> during market hours.</li>
          <li>AlphaScout rebalances <span className="text-foreground font-medium">twice daily</span> (07:30 & 16:00 IST).</li>
        </ul>
        <p className="pt-1 text-[11px] italic">
          That's it — you're set. You can replay this tour anytime by running
          {' '}<code className="font-mono text-foreground/80">localStorage.removeItem('dashboard_tour_seen')</code>{' '}
          in the browser console.
        </p>
      </>
    ),
  },
];

const WelcomeTour = ({ forceOpen = false }) => {
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }
    try {
      const seen = localStorage.getItem(TOUR_STORAGE_KEY);
      if (seen !== '1') {
        // Tiny delay so the dashboard renders behind it first.
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch (_) {
      /* localStorage disabled — silently skip */
    }
  }, [forceOpen]);

  const dismiss = (reason) => {
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, '1');
    } catch (_) { /* ignore */ }
    setOpen(false);
    setIdx(0);
    // reason kept for future analytics; intentionally not sent anywhere.
    void reason;
  };

  if (!open) return null;
  const slide = SLIDES[idx];
  const isLast = idx === SLIDES.length - 1;
  const Icon = slide.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
      data-testid="welcome-tour-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Welcome tour"
    >
      <div className="bg-surface border border-border rounded-lg max-w-lg w-full shadow-2xl relative">
        <button
          type="button"
          onClick={() => dismiss('close')}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted/40"
          data-testid="welcome-tour-close"
          aria-label="Close tour"
        >
          <X className="w-4 h-4" />
        </button>

        <Slide icon={Icon} title={slide.title}>{slide.body}</Slide>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 px-6 pb-3">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70'
              }`}
              data-testid={`welcome-tour-dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border/60 px-6 py-3 bg-muted/10">
          <button
            type="button"
            onClick={() => dismiss('skip')}
            className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
            data-testid="welcome-tour-skip"
          >
            Skip tour
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={idx === 0}
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              className="inline-flex items-center gap-1 text-xs text-foreground border border-border rounded px-2.5 py-1 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-muted/40"
              data-testid="welcome-tour-prev"
            >
              <ChevronLeft className="w-3 h-3" /> Back
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={() => dismiss('finished')}
                className="inline-flex items-center gap-1 text-xs font-medium bg-primary text-primary-foreground rounded px-3 py-1 hover:bg-primary/90"
                data-testid="welcome-tour-done"
              >
                <ShieldCheck className="w-3 h-3" /> Got it
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIdx((i) => Math.min(SLIDES.length - 1, i + 1))}
                className="inline-flex items-center gap-1 text-xs font-medium bg-primary text-primary-foreground rounded px-3 py-1 hover:bg-primary/90"
                data-testid="welcome-tour-next"
              >
                Next <ChevronRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTour;
